import React from 'react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Badge } from '../../../../components/ui/badge';
import { TabsContent } from '../../../../components/ui/tabs';
import { InvestorPicker } from '../../../../components/InvestorPicker';
import { ResizableTableHead } from '../../../../components/ResizableTableHead';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Search,
  Edit2,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  TRANSACTION_TYPES,
  getTransactionConfig,
  formatCurrency,
  formatDate,
} from '../utils/formatters';

export function TransactionsTabPanel({ ctx }) {
  const {
    transactions,
    investors,
    investorFilter, setInvestorFilter,
    typeFilter, setTypeFilter,
    startDate, setStartDate,
    endDate, setEndDate,
    fetchTransactions,
    clearFilters,
    selectedMain, setSelectedMain,
    setBulkEditTarget, setBulkEditOpen,
    handleBulkDelete, bulkDeleting,
    sortTxn, setSortTxn, toggleSort,
    toggleSelectAll, toggleSelect,
    handleEdit, handleDelete,
    pagination, setPagination,
  } = ctx;

  return (
    <TabsContent value="transactions" className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500">Investor</Label>
              <InvestorPicker
                value={investorFilter}
                onChange={setInvestorFilter}
                investors={investors.map(i => ({
                  id: i.id,
                  name: `${i.first_name || ''} ${i.last_name || ''}`.trim() || i.entity_name || i.email || i.id,
                  rp_code: i.risk_participant_code || '',
                  email: i.email || '',
                }))}
                includeAll
                placeholder="All Investors"
                className="w-full"
                testId="filter-investor"
              />
            </div>
            <div className="w-[180px]">
              <Label className="text-xs text-gray-500">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="filter-type"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {TRANSACTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]"><Label className="text-xs text-gray-500">From</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="filter-start-date" /></div>
            <div className="w-[150px]"><Label className="text-xs text-gray-500">To</Label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="filter-end-date" /></div>
            <Button variant="outline" onClick={fetchTransactions} data-testid="apply-filter-btn"><Search className="h-4 w-4 mr-2" />Search</Button>
            {(investorFilter !== 'all' || typeFilter !== 'all' || startDate || endDate) && <Button variant="ghost" onClick={clearFilters}><X className="h-4 w-4 mr-1" />Clear</Button>}
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar for Transactions tab */}
      {selectedMain.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded">
          <span className="text-sm font-medium">{selectedMain.size} selected</span>
          <Button size="sm" variant="outline" className="text-blue-600 border-blue-200" data-testid="bulk-edit-transactions" onClick={() => { setBulkEditTarget('committed'); setBulkEditOpen(true); }}>
            <Edit2 className="h-3 w-3 mr-1" />Bulk Edit
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleBulkDelete(selectedMain, setSelectedMain)} disabled={bulkDeleting} data-testid="bulk-delete-transactions"><Trash2 className="h-3 w-3 mr-1" />{bulkDeleting ? 'Deleting...' : 'Delete Selected'}</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedMain(new Set())}>Clear</Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0 overflow-auto">
          {(() => { const sorted = transactions; return (
          <Table style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-10"><input type="checkbox" checked={transactions.length > 0 && selectedMain.size === transactions.length} onChange={() => toggleSelectAll(setSelectedMain, transactions.map(t => t.id))} className="rounded" data-testid="select-all-main" /></TableHead>
                <ResizableTableHead storageKey="txns:date" defaultWidth={130} sortable sortKey="transaction_date" sortState={sortTxn} onSort={toggleSort(setSortTxn)}>Date</ResizableTableHead>
                <ResizableTableHead storageKey="txns:investor" defaultWidth={240} sortable sortKey="investor_name" sortState={sortTxn} onSort={toggleSort(setSortTxn)}>Investor</ResizableTableHead>
                <ResizableTableHead storageKey="txns:type" defaultWidth={170} sortable sortKey="transaction_type" sortState={sortTxn} onSort={toggleSort(setSortTxn)}>Type</ResizableTableHead>
                <ResizableTableHead storageKey="txns:amount" defaultWidth={150} sortable sortKey="amount" sortState={sortTxn} onSort={toggleSort(setSortTxn)} className="text-right">Amount</ResizableTableHead>
                <ResizableTableHead storageKey="txns:reference" defaultWidth={260} sortable sortKey="reference" sortState={sortTxn} onSort={toggleSort(setSortTxn)}>Reference</ResizableTableHead>
                <ResizableTableHead storageKey="txns:notes" defaultWidth={260}>Notes</ResizableTableHead>
                <TableHead style={{ width: 110, minWidth: 110 }}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-gray-500">No transactions found</TableCell></TableRow>
              ) : sorted.map((txn) => {
                const config = getTransactionConfig(txn.transaction_type);
                return (
                  <TableRow key={txn.id} className={`hover:bg-gray-50 ${selectedMain.has(txn.id) ? 'bg-amber-50' : ''}`}>
                    <TableCell><input type="checkbox" checked={selectedMain.has(txn.id)} onChange={() => toggleSelect(setSelectedMain)(txn.id)} className="rounded" data-testid={`select-main-${txn.id}`} /></TableCell>
                    <TableCell className="font-medium">{formatDate(txn.transaction_date)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{txn.risk_participant_code || txn.investor_id || '-'}{txn.investor_name && txn.investor_name !== txn.investor_id ? ` — ${txn.investor_name}` : ''}</p>
                        <p className="text-xs text-gray-500">{txn.investor_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <Badge className={config.color}>{config.label}</Badge>
                        {txn.fee_breakdown && (txn.transaction_type === 'fee_mgmt' || txn.transaction_type === 'fee_perf') && (
                          <span className="text-[10px] text-gray-500" data-testid={`fee-vat-badge-${txn.id}`}>
                            ex-VAT · +VAT {formatCurrency(txn.fee_breakdown.vat_amount)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium"><span className={txn.transaction_type === 'investment' ? 'text-green-600' : ''}>{formatCurrency(txn.amount)}</span></TableCell>
                    <TableCell className="text-sm text-gray-600 truncate" title={txn.loan_id || txn.reference || ''}>
                      {txn.loan_id ? <span><span className="font-mono text-xs text-indigo-700">{txn.loan_id}</span>{txn.reference ? <span className="text-gray-500"> · {txn.reference}</span> : null}</span> : (txn.reference || '-')}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 truncate" title={txn.notes || ''}>{txn.notes || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(txn)} data-testid={`edit-${txn.id}`}><Edit2 className="h-4 w-4 text-gray-500" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(txn)} data-testid={`delete-${txn.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          ); })()}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-gray-500">Showing {((pagination.page-1)*pagination.pageSize)+1} to {Math.min(pagination.page*pagination.pageSize, pagination.total)} of {pagination.total}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({...p, page: p.page-1}))} disabled={pagination.page===1}><ChevronLeft className="h-4 w-4" /></Button>
                <span className="px-3 py-1 text-sm">{pagination.page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({...p, page: p.page+1}))} disabled={pagination.page===pagination.totalPages}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
